import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Grid, Typography, Button, Stack, Divider, Paper } from '@mui/material';
import { AddCircleOutline as AddCircleOutlineIcon } from '@mui/icons-material';
import AttachmentsSection from './Attachments';
import ClaimField from './ClaimField';
import PayoutCard from './PayoutCard';
import ClaimCRU from './ClaimCRU';
import ApprovalHeader from './ClaimApprovalHeader';
import SectionDivider from './SectionDivider';
import { PageLoader } from '../../../shared_elements';
import { ShowAttachments } from '../../../shared_elements/components';
import { trackActivity } from '../../../utils/mixpanel';
import { PermissionWrapper } from '../../../wrappers';
import { enqueueSnackbar } from 'notistack';
import fileUpload, { getApiResponse } from '../../../utils/fileUpload';
import { useSelector } from 'react-redux';
import FileUploadLayout from '../../../shared_elements/components/FileUploadLayout';

const EmptyClaimsState = ({ onAddClaim }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        border: '1px dashed grey.400',
        borderRadius: '8px',
        textAlign: 'center',
        minHeight: '10px',
      }}
    >
      <Typography variant="h6" component="p" gutterBottom>
        No Claims Found
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Get started by adding your Claim
      </Typography>
      <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "shop_visit", operationType: ['U'] }}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => { onAddClaim(); trackActivity('Item Clicked', { page_title: `Shop Visit Claim`, page_type: 'Add Claim Button', }) }}>
          Add Claim
        </Button>
      </PermissionWrapper>
    </Box>
  );
};

const DisabledClaimsState = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      border: '1px dashed #00000042',
      borderRadius: '4px',
      textAlign: 'center',
      minHeight: '80px',
      backgroundColor: '#f5f5f5',
    }}
  >
    <Typography variant="body1" color="text.secondary">
      No Claims Found
    </Typography>
    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
      To process a claim, Shop Visit cost must be added first.
    </Typography>
  </Box>
);

const ClaimCard = ({ shopVisit, updatedAsset, llpList, onTabChange, getShopVisit, getShopVisitFundBalance, onLoadingChange, claimInfo, claimListLoader, getClaimList }) => {
  const params = useParams()
  const [addEditClaim, setAddEditClaim] = useState({ modal: false, data: { shop_visit_id: shopVisit?.id, submission_date: null, claim_amount: null } })
  const { fileUploadDetails } = useSelector((state) => state.leases);
  const [isLoading, setLoading] = useState({ buttonLoader: false })
  const [uploadfileList, setUploadfileList] = useState({ mode: '', modal: false, data: null });
  const [uploadFileProgressData, setUploadFileProgressData] = useState({})
  const [uploadFileStatus, setUploadFileStatus] = useState({ mode: '', modal: false, data: null });
  const [slug, setSlug] = useState({})
  useEffect(() => {
    onLoadingChange && onLoadingChange(!!claimListLoader?.skeletonLoader)
    return () => { onLoadingChange && onLoadingChange(false) }
  }, [claimListLoader?.skeletonLoader])
  const llpClaimTotal = (llpList || []).reduce((sum, item) => sum + (Number(item?.claim_amount) || 0), 0);
  const onDeleteAttachments = (deltedslug) => {
    setUploadfileList({
      ...uploadfileList, data: {
        ...uploadfileList.data,
        attachments: uploadfileList.data.attachments.filter(item => item.slug !== deltedslug)
      }
    });
    if (addEditClaim?.modal) {
      setAddEditClaim({
        ...addEditClaim,
        data: {
          ...addEditClaim.data,
          attachments: addEditClaim.data.attachments.filter(item => item?.slug !== deltedslug)
        }
      })
    }
    getClaimList("pageLoader")
  }
  const handleUpload = async (slug, mode, key = null) => {
        setSlug({ slug: slug })
        await fileUpload({
            attachments: fileUploadDetails.attachments,
            module_name: key ? key : "shop_visit_claim",
            slug: slug,
            query: { record_action_type: mode },
            onStart: () => setLoading(true),
            onComplete: () => setLoading(false),
            onProgress: ({ progressRefs, attachments, response }) => {
                setUploadFileProgressData({ progressRefs, attachments, response });
            },
            onSuccess: (success_msg) => {
                enqueueSnackbar(success_msg, {
                    variant: "success",
                    anchorOrigin: { vertical: "top", horizontal: "right" }
                });
                getClaimList("pageLoader")
            },
            onError: (err) => {
                enqueueSnackbar(`${err?.message}` || "Error while uploading files", {
                    variant: "error",
                    anchorOrigin: { vertical: "top", horizontal: "right" }
                });
            }
        });
        setSlug({})
    };
  return (
    <>
      {claimInfo?.list?.length > 0 ? claimInfo?.list?.map(claimData => {
        const uploadedFileData = getApiResponse("Parties", claimData?.slug);
        return (<>
          {updatedAsset?.asset_type !== "engine" && <SectionDivider label="Claim"></SectionDivider>}
          {shopVisit?.status && <Paper
            elevation={2}
            sx={{
              p: 1.5,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2
            }}
          >
            <ApprovalHeader isLoading={isLoading} claimListLoader={claimListLoader} updatedAsset={updatedAsset} onEdit={() => { setAddEditClaim({ modal: true, mode: 'edit', data: claimData }) }} getClaimList={getClaimList} shopVisit={shopVisit} claimData={claimData} claimInfo={claimInfo} fileUpload={handleUpload} onTabChange={onTabChange} onProceedSuccess={() => { getShopVisit ? getShopVisit() : getShopVisitFundBalance && getShopVisitFundBalance(shopVisit); }} />
          </Paper>}
          <Grid container spacing={1} sx={{ display: 'flex', flexDirection: "row", justifyContent: 'space-between' }}>
            <Grid size={{ xs: 12, md: 8.8 }}>
              <Paper sx={{ p: 2 }}  >
                <ClaimField claim={claimData} shopVisit={shopVisit} llpClaimTotal={llpClaimTotal} />
              </Paper>
              {claimData?.status?.value === 3 ? <PayoutCard claim={claimData} shopVisit={shopVisit} getResponseBack={() => { getClaimList("pageLoader"); getShopVisit ? getShopVisit() : getShopVisitFundBalance && getShopVisitFundBalance(shopVisit); }} /> : null}
            </Grid>
            <Grid size={{ xs: 12, md: 3.2 }}>
              <AttachmentsSection
                claim={claimData}
                slug={slug?.slug}
                attachmentData={claimData?.attachments}
                viewFileStatus={() => setUploadFileStatus({ modal: true, data: uploadedFileData, mode: 'view' })} />
            </Grid>
          </Grid>
        </>)
      })
        : null}
      <Grid mt={1}>
        {claimInfo?.list?.length === 0 ? (
          Number(shopVisit?.cost) > 0 ? (
            <Grid size={{ xs: 12, md: 12 }}>
              <Box sx={{ border: "1px solid #00000042", borderRadius: "4px" }}>
                <EmptyClaimsState onAddClaim={() => setAddEditClaim({ modal: true, mode: 'add', data: { shop_visit_id: shopVisit?.id, submission_date: null, claim_amount: null,  } })} />
              </Box>
            </Grid>
          ) : (
            <Grid size={{ xs: 12, md: 12 }}>
              <DisabledClaimsState />
            </Grid>
          )
        ) : null}
      </Grid>
      {addEditClaim?.modal ?
        <ClaimCRU
          params={params}
          shopVisit={shopVisit}
          addEditClaim={addEditClaim}
          modeType={addEditClaim.mode}
          updatedAsset={updatedAsset}
          getResponseBack={() => getClaimList("pageLoader")}
          onOpenAttachments={() => setUploadfileList({ modal: true, data: addEditClaim?.data, mode: 'view' })}
          onToggleModal={() => setAddEditClaim({ modal: false, mode: '', data: null })}
          llpClaimTotal={llpClaimTotal}
          fileUpload={handleUpload}
        /> : null}
      {
        uploadfileList.modal ? <ShowAttachments
          moduleName="Claim"
          url='lease/shop-visit-claim'
          attachmentsData={uploadfileList}
          getResponseBack={onDeleteAttachments}
          toggleModalFn={() => setUploadfileList({ modal: false, data: null, mode: '' })}
          onViewDisabled={addEditClaim?.mode === 'view' || uploadfileList?.mode === 'view'}
          permissionKey="shop_visit"
        /> : null
      }
      {
        <FileUploadLayout
          uploadFileStatus={uploadFileStatus}
          open={uploadFileStatus?.modal}
          setUploadFileStatus={setUploadFileStatus}
          uploadFileProgressData={uploadFileProgressData}
          progressRefs={uploadFileProgressData?.progressRefs}
          attachments={uploadFileProgressData?.attachments}
          onDownloadComplete={() => setSlug({})}
        />
      }
      {claimListLoader?.pageLoader ? <PageLoader /> : null}
    </>
  )
}

export default ClaimCard;